import {
  ProviderType,
  TypedTransactionReceipt,
  WarpCore,
  WarpTxCategory,
  WarpTypedTransaction,
} from "@hyperlane-xyz/sdk";
import { ProtocolType, toTitleCase, toWei } from "@hyperlane-xyz/utils";
import {
  getAccountAddressForChain,
  useEthereumAccount,
  useEthereumActiveChain,
  useEthereumTransactionFns,
} from "@hyperlane-xyz/widgets";
import {
  getConnectorClient,
  getGasPrice,
  waitForTransactionReceipt,
} from "@wagmi/core";
import { getChainDisplayName } from "modules/bridge/hyperlane/features/chains/utils";
import { useCallback, useMemo, useState } from "react";
import { numberToHex, type Hex } from "viem";
import { useConfig } from "wagmi";
import { logger } from "../../utils/logger";
import { useMultiProvider } from "../chains/hooks";
import { AppState, useStore } from "../store";
import { getTokenByIndex, useWarpCore } from "../tokens/hooks";
import { TransferContext, TransferFormValues, TransferStatus } from "./types";
import { tryGetMsgIdFromTransferReceipt } from "./utils";

// Prom is a Polygon CDK chain that rejects EIP-1559 (type-2) transactions
// ("only legacy transactions are supported"). `@hyperlane-xyz/widgets`
// forwards whatever fee fields the ethers provider populates, which on a
// post-London RPC defaults to type-2. We override that leg to send legacy.
const LEGACY_ONLY_CHAINS = new Set<string>(["prom"]);

const CHAIN_MISMATCH_ERROR = "ChainMismatchError";
const TRANSFER_TIMEOUT_ERROR1 = "block height exceeded";
const TRANSFER_TIMEOUT_ERROR2 = "timeout";

export function useTokenTransfer(onDone?: () => void) {
  const { transfers, addTransfer, updateTransferStatus } = useStore((s) => ({
    transfers: s.transfers,
    addTransfer: s.addTransfer,
    updateTransferStatus: s.updateTransferStatus,
  }));
  const transferIndex = transfers.length;

  const multiProvider = useMultiProvider();
  const warpCore = useWarpCore();

  const accountInfo = useEthereumAccount(multiProvider);
  const activeChain = useEthereumActiveChain(multiProvider);
  const baseTransactionFns = useEthereumTransactionFns(multiProvider);
  const wagmiConfig = useConfig();

  const transactionFns = useMemo(
    () => ({
      ...baseTransactionFns,
      sendTransaction: async (params: {
        tx: WarpTypedTransaction;
        chainName: string;
        activeChainName?: string;
      }) => {
        if (!LEGACY_ONLY_CHAINS.has(params.chainName)) {
          return baseTransactionFns.sendTransaction(params);
        }
        logger.debug("Forcing legacy tx for chain", params.chainName);
        if (params.tx.type !== ProviderType.EthersV5) {
          throw new Error(`Unsupported tx type: ${params.tx.type}`);
        }
        if (
          params.activeChainName &&
          params.activeChainName !== params.chainName
        ) {
          await baseTransactionFns.switchNetwork!(params.chainName);
        }
        const ethersTx = params.tx.transaction;
        const chainId = multiProvider.getChainMetadata(params.chainName)
          .chainId as number;
        // Bypass viem's sendTransaction formatter — it sends `type: '0x0'`
        // alongside `gasPrice`, but MetaMask detects `eth_feeHistory` support
        // on Prom's RPC and overrides to type-2 anyway. Instead, call
        // `eth_sendTransaction` on the wallet provider directly with ONLY
        // `gasPrice` and no `type`/`maxFeePerGas` fields — with no 1559 fee
        // fields present, MetaMask has nothing to promote and emits legacy.
        const gasPriceOverride = ethersTx.gasPrice
          ? BigInt(ethersTx.gasPrice.toString())
          : await getGasPrice(wagmiConfig, { chainId });
        const client = await getConnectorClient(wagmiConfig, { chainId });
        const gasLimit = ethersTx.gasLimit
          ? BigInt(ethersTx.gasLimit.toString())
          : undefined;
        const valueBi = ethersTx.value
          ? BigInt(ethersTx.value.toString())
          : undefined;
        // Explicitly set `type: '0x0'` on the raw RPC params. MetaMask's
        // TransactionController only runs its EIP-1559 envelope override when
        // `!txParams.type` — setting type explicitly short-circuits that path.
        // viem's formatTransactionRequest does NOT forward the `type` key, so
        // we have to include it ourselves via a direct `eth_sendTransaction`.
        const rpcParams: Record<string, string> = {
          from: client.account.address,
          to: ethersTx.to as Hex,
          type: "0x0",
          gasPrice: numberToHex(gasPriceOverride),
        };
        if (ethersTx.data) rpcParams.data = ethersTx.data as Hex;
        if (valueBi !== undefined) rpcParams.value = numberToHex(valueBi);
        if (gasLimit !== undefined) rpcParams.gas = numberToHex(gasLimit);
        if (ethersTx.nonce !== undefined) {
          rpcParams.nonce = numberToHex(ethersTx.nonce);
        }
        const hash = (await client.request({
          method: "eth_sendTransaction" as const,
          params: [rpcParams],
        } as any)) as Hex;
        const confirm = () =>
          waitForTransactionReceipt(wagmiConfig, {
            chainId,
            hash,
            confirmations: 1,
          }).then((r) => ({
            type: ProviderType.Viem as const,
            receipt: { ...r, contractAddress: r.contractAddress || null },
          }));
        return { hash, confirm };
      },
    }),
    [baseTransactionFns, multiProvider, wagmiConfig]
  );

  const [isLoading, setIsLoading] = useState(false);

  // TODO implement cancel callback for when modal is closed?
  const triggerTransactions = useCallback(
    (values: TransferFormValues) =>
      executeTransfer({
        warpCore,
        values,
        transferIndex,
        accountInfo,
        activeChain,
        transactionFns,
        addTransfer,
        updateTransferStatus,
        setIsLoading,
        onDone,
      }),
    [
      warpCore,
      transferIndex,
      accountInfo,
      activeChain,
      transactionFns,
      setIsLoading,
      addTransfer,
      updateTransferStatus,
      onDone,
    ]
  );

  return {
    isLoading,
    triggerTransactions,
  };
}

async function executeTransfer({
  warpCore,
  values,
  transferIndex,
  accountInfo,
  activeChain,
  transactionFns,
  addTransfer,
  updateTransferStatus,
  setIsLoading,
  onDone,
}: {
  warpCore: WarpCore;
  values: TransferFormValues;
  transferIndex: number;
  accountInfo: ReturnType<typeof useEthereumAccount>;
  activeChain: ReturnType<typeof useEthereumActiveChain>;
  transactionFns: ReturnType<typeof useEthereumTransactionFns>;
  addTransfer: (t: TransferContext) => void;
  updateTransferStatus: AppState["updateTransferStatus"];
  setIsLoading: (b: boolean) => void;
  onDone?: () => void;
}) {
  logger.debug("Preparing transfer transaction(s)");
  setIsLoading(true);
  let transferStatus: TransferStatus = TransferStatus.Preparing;
  updateTransferStatus(transferIndex, transferStatus);

  const { origin, destination, tokenIndex, amount, recipient } = values;
  const multiProvider = warpCore.multiProvider;

  try {
    const originToken = getTokenByIndex(warpCore, tokenIndex);
    const connection = originToken?.getConnectionForChain(destination);
    if (!originToken || !connection)
      throw new Error("No token route found between chains");

    const isNft = originToken.isNft();
    const weiAmountOrId = isNft ? amount : toWei(amount, originToken.decimals);
    const originTokenAmount = originToken.amount(weiAmountOrId);

    const sendTransaction = transactionFns.sendTransaction;
    const sender = getAccountAddressForChain(multiProvider, origin, {
      ethereum: accountInfo,
      sealevel: {
        addresses: [],
        protocol: ProtocolType.Sealevel,
        isReady: true,
      },
      cosmos: { addresses: [], protocol: ProtocolType.Cosmos, isReady: true },
    });
    if (!sender) throw new Error("No active account found for origin chain");

    const isCollateralSufficient =
      await warpCore.isDestinationCollateralSufficient({
        originTokenAmount,
        destination,
      });
    if (!isCollateralSufficient) {
      // toast.error('Insufficient collateral on destination for transfer');
      throw new Error("Insufficient destination collateral");
    }

    addTransfer({
      timestamp: new Date().getTime(),
      status: TransferStatus.Preparing,
      origin,
      destination,
      originTokenAddressOrDenom: originToken.addressOrDenom,
      destTokenAddressOrDenom: connection.token.addressOrDenom,
      sender,
      recipient,
      amount,
    });

    updateTransferStatus(
      transferIndex,
      (transferStatus = TransferStatus.CreatingTxs)
    );

    const txs = await warpCore.getTransferRemoteTxs({
      originTokenAmount,
      destination,
      sender,
      recipient,
    });

    const hashes: string[] = [];
    let txReceipt: TypedTransactionReceipt | undefined = undefined;
    for (const tx of txs) {
      updateTransferStatus(
        transferIndex,
        (transferStatus = txCategoryToStatuses[tx.category][0])
      );
      const { hash, confirm } = await sendTransaction({
        tx,
        chainName: origin,
        activeChainName: activeChain.chainName,
      });
      updateTransferStatus(
        transferIndex,
        (transferStatus = txCategoryToStatuses[tx.category][1])
      );
      txReceipt = await confirm();
      const description = toTitleCase(tx.category);
      logger.debug(`${description} transaction confirmed, hash:`, hash);
      // toastTxSuccess(`${description} transaction sent!`, hash, origin);
      hashes.push(hash);
    }

    const msgId = txReceipt
      ? tryGetMsgIdFromTransferReceipt(multiProvider, origin, txReceipt)
      : undefined;

    updateTransferStatus(
      transferIndex,
      (transferStatus = TransferStatus.ConfirmedTransfer),
      {
        originTxHash: hashes.at(-1),
        msgId,
      }
    );
  } catch (error: any) {
    logger.error(`Error at stage ${transferStatus}`, error);
    const errorDetails = error.message || error.toString();
    updateTransferStatus(transferIndex, TransferStatus.Failed);
    if (errorDetails.includes(CHAIN_MISMATCH_ERROR)) {
      // Wagmi switchNetwork call helps prevent this but isn't foolproof
      throw new Error("Wallet must be connected to origin chain");
    } else if (
      errorDetails.includes(TRANSFER_TIMEOUT_ERROR1) ||
      errorDetails.includes(TRANSFER_TIMEOUT_ERROR2)
    ) {
      throw new Error(
        `Transaction timed out, ${getChainDisplayName(
          multiProvider,
          origin
        )} may be busy. Please try again.`
      );
    } else {
      throw new Error(
        errorMessages[transferStatus] || "Unable to transfer tokens."
      );
    }
  }

  setIsLoading(false);
  if (onDone) onDone();
}

const errorMessages: Partial<Record<TransferStatus, string>> = {
  [TransferStatus.Preparing]: "Error while preparing the transactions.",
  [TransferStatus.CreatingTxs]: "Error while creating the transactions.",
  [TransferStatus.SigningApprove]:
    "Error while signing the approve transaction.",
  [TransferStatus.ConfirmingApprove]:
    "Error while confirming the approve transaction.",
  [TransferStatus.SigningTransfer]:
    "Error while signing the transfer transaction.",
  [TransferStatus.ConfirmingTransfer]:
    "Error while confirming the transfer transaction.",
};

const txCategoryToStatuses: Record<
  WarpTxCategory,
  [TransferStatus, TransferStatus]
> = {
  [WarpTxCategory.Approval]: [
    TransferStatus.SigningApprove,
    TransferStatus.ConfirmingApprove,
  ],
  [WarpTxCategory.Transfer]: [
    TransferStatus.SigningTransfer,
    TransferStatus.ConfirmingTransfer,
  ],
};
